import * as React from 'react'
import { cn } from '@/lib/cn'

/**
 * The page's vertical rhythm, in one place.
 *
 * Before this, every section on the site was a bare `<section className="space-y-8">`
 * on the same flat canvas, which is the structural reason the site read as a wall
 * of text: nothing told your eye where one idea stopped and the next began.
 *
 * `tone` bands the page. Alternating canvas -> raised -> canvas gives scrolling a
 * pulse without adding any decoration. Note `raised` is a real surface token, NOT
 * `bg-muted/30` — `muted` is a light-grey INK, and using it as a surface is what
 * washed the old platform pages out to flat grey.
 */
const TONES = {
  /** Sits on the page canvas. The default. */
  canvas: 'bg-transparent',
  /** A banded section, one step up the surface ramp. */
  raised: 'bg-surface',
  /** Recessed — for asides and fine print, not for primary content. */
  sunken: 'bg-surface-sunken',
} as const

export type SectionTone = keyof typeof TONES

interface SectionProps extends React.ComponentProps<'section'> {
  tone?: SectionTone
  /** Short mono/brass kicker above the heading. Two or three words. */
  eyebrow?: string
  heading?: React.ReactNode
  /** One sentence under the heading. If you need two, the second belongs in the body. */
  lede?: React.ReactNode
  /** Center the heading block. Off by default — left-aligned reads faster. */
  centered?: boolean
  /** Full-bleed background while content stays in the measure. */
  bleed?: boolean
  /**
   * `compact` drops the heading a step and tightens the header gap. For
   * secondary sections (fine print, terms) where a 64px display heading
   * outweighs what it introduces.
   */
  size?: 'default' | 'compact'
  /**
   * Atmospheric background image, held well behind the content. Use sparingly:
   * one per page at most, on a banded section. It is decorative by definition
   * (the section's meaning is in its text), so it renders with empty alt.
   */
  backdrop?: string
  /** Object-position for the backdrop, e.g. `'center 30%'`. */
  backdropPosition?: string
}

/**
 * `forwardRef` is load-bearing, not ceremony.
 *
 * The scroll-reveal hooks in `@gjl/motion/css` work by holding a ref to a
 * container and querying it for children to animate. On React 18 a ref passed to
 * a plain function component is stripped before the component is called — it
 * never reaches the `<section>`, `containerRef.current` stays null, and
 * `useScrollReveal` early-returns. The content stays visible, so nothing looks
 * broken; the reveals just quietly stop happening, and TypeScript does not catch
 * it because `ComponentProps<'section'>` includes `ref` in its type.
 */
export const Section = React.forwardRef<HTMLElement, SectionProps>(function Section(
  {
    tone = 'canvas',
    eyebrow,
    heading,
    lede,
    centered = false,
    bleed = false,
    size = 'default',
    backdrop,
    backdropPosition = 'center',
    className,
    children,
    ...props
  },
  ref
) {
  const hasHeader = Boolean(eyebrow || heading || lede)
  const compact = size === 'compact'
  return (
    <section
      ref={ref}
      className={cn(
        'scroll-mt-24',
        backdrop && 'relative isolate overflow-hidden',
        TONES[tone],
        // EVERY section carries its own vertical rhythm, banded or not.
        // Previously only banded sections had padding, so when a full-bleed band
        // ended the next section started flush against its edge with no air.
        compact ? 'py-14 md:py-20' : 'py-16 md:py-24',
        bleed && tone !== 'canvas' && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
        className
      )}
      {...props}
    >
      {backdrop && (
        <>
          <img
            src={backdrop}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className="absolute inset-0 -z-10 h-full w-full object-cover opacity-[0.18]"
            style={{ objectPosition: backdropPosition }}
          />
          {/* Scrim. Without it the image competes with body copy for contrast;
              the section's own tone is only 0.195 lightness, so anything showing
              through has to be pushed back under it. */}
          <div
            aria-hidden
            className="from-background/85 via-background/70 to-background/90 absolute inset-0 -z-10 bg-linear-to-b"
          />
        </>
      )}
      <div className={cn(bleed && 'mx-auto max-w-6xl px-4')}>
        {hasHeader && (
          <header className={cn('space-y-3', compact ? 'mb-8 md:mb-10' : 'mb-10 md:mb-14', centered && 'text-center')}>
            {eyebrow && <Eyebrow centered={centered}>{eyebrow}</Eyebrow>}
            {heading &&
              (compact ? <h2 className="text-fluid-h3">{heading}</h2> : <h2 className="text-fluid-h2">{heading}</h2>)}
            {lede && (
              <p className={cn('text-fluid-body-lg text-muted-foreground max-w-2xl', centered && 'mx-auto')}>{lede}</p>
            )}
          </header>
        )}
        {children}
      </div>
    </section>
  )
})

/**
 * Mono + brass section kicker.
 *
 * Brass is the "consequence" accent, and this is the one place the brand's
 * second colour lands on every page, which is what stops the site reading as
 * monochrome. Exported so pages can place one outside a Section.
 *
 * NO leading rule. It carried a `h-px w-8` brass dash, and between this and the
 * per-item rules in <VoiceEntryList> the page filled up with small horizontal
 * dashes. The mono tracking and the colour already mark this as a label; the
 * dash was doing nothing the type wasn't.
 */
export function Eyebrow({ children, centered = false }: { children: React.ReactNode; centered?: boolean }) {
  return (
    <p className={cn('text-accent font-mono text-xs tracking-[0.18em] uppercase', centered && 'text-center')}>
      {children}
    </p>
  )
}
